import { Moon, Sun, Monitor, Palette, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu'
import { useTheme } from '@/context/theme-context'
import { cn } from '@/lib/utils'

export function ThemeToggle() {
  const { theme, setTheme, actualTheme, isTransitioning, toggleTheme } = useTheme()

  const getThemeIcon = () => {
    if (isTransitioning) {
      return <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
    }
    return actualTheme === 'dark' ?
      <Moon className="h-4 w-4" /> :
      <Sun className="h-4 w-4" />
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "dark-mode-toggle relative h-9 w-9 transition-all duration-300",
            "hover:bg-primary/10 hover:text-primary",
            "dark:hover:bg-primary/20 dark:hover:text-primary",
            isTransitioning && "opacity-70 cursor-wait"
          )}
          disabled={isTransitioning}
        >
          {getThemeIcon()}
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={cn(
          "glass-card min-w-[160px] p-2",
          "bg-card-bg border-card-border shadow-lg",
          "animate-in slide-in-from-top-2 duration-200"
        )}
      >
        <DropdownMenuLabel className="text-xs font-medium text-text-secondary px-2 py-1">
          Appearance
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-cv-border" />

        <DropdownMenuItem
          onClick={() => setTheme('light')}
          className={cn(
            "flex items-center justify-between cursor-pointer rounded-lg px-2 py-2 text-sm",
            "hover:bg-primary/10 hover:text-primary transition-colors",
            theme === 'light' && "bg-primary/10 text-primary font-medium"
          )}
        >
          <div className="flex items-center">
            <Sun className="mr-3 h-4 w-4" />
            <span>Light</span>
          </div>
          {theme === 'light' && <Check className="h-3 w-3" />}
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => setTheme('dark')}
          className={cn(
            "flex items-center justify-between cursor-pointer rounded-lg px-2 py-2 text-sm",
            "hover:bg-primary/10 hover:text-primary transition-colors",
            theme === 'dark' && "bg-primary/10 text-primary font-medium"
          )}
        >
          <div className="flex items-center">
            <Moon className="mr-3 h-4 w-4" />
            <span>Dark</span>
          </div>
          {theme === 'dark' && <Check className="h-3 w-3" />}
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => setTheme('system')}
          className={cn(
            "flex items-center justify-between cursor-pointer rounded-lg px-2 py-2 text-sm",
            "hover:bg-primary/10 hover:text-primary transition-colors",
            theme === 'system' && "bg-primary/10 text-primary font-medium"
          )}
        >
          <div className="flex items-center">
            <Monitor className="mr-3 h-4 w-4" />
            <span>System</span>
          </div>
          {theme === 'system' && <Check className="h-3 w-3" />}
        </DropdownMenuItem>

        <DropdownMenuSeparator className="bg-cv-border my-2" />

        <DropdownMenuItem
          onClick={toggleTheme}
          className={cn(
            "flex items-center cursor-pointer rounded-lg px-2 py-2 text-sm",
            "hover:bg-brand-secondary/10 hover:text-brand-secondary transition-colors"
          )}
        >
          <Palette className="mr-3 h-4 w-4" />
          <span>Quick Toggle</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}